import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import styles from './styles.module.scss';
import addVariants from '../utils/addVariants/index.ts';
import useBoolean from '../utils/useBoolean/index.ts';

export const VARIANTS = ['default', 'full-screen', 'no-border', 'dark', 'black'];
const Drawer = ({
  page,
  children,
  onChange,
  variant: variants,
  isToRight,
  className,
  width,
  minWidth,
  isOpen: isOpenFromProps,
  isOpenByDefault,
  isPermanent,
  ...props
}) => {
  const {
    value: isOpen,
    toggle,
    setTrue,
    setFalse
  } = useBoolean(isOpenByDefault, {
    onChange,
    overrideWith: isPermanent || isOpenFromProps
  });
  const currentState = useMemo(
    () => ({
      isOpen,
      openDrawer: setTrue,
      closeDrawer: setFalse,
      toggleDrawer: toggle
    }),
    [isOpen, toggle, setTrue, setFalse]
  );
  const sidebarContentEl = useMemo(() => {
    let sidebar = children;

    if (typeof sidebar === 'function') {
      sidebar = children(currentState);
    }

    return sidebar;
  }, [children, currentState]);
  const mainContentEl = useMemo(() => {
    let pageContentEl = page;

    if (typeof pageContentEl === 'function') {
      pageContentEl = page(currentState);
    }

    let mainContent = null;

    if (pageContentEl) {
      mainContent = (
        <main
          className={classnames(
            addVariants('aglmd-content', styles, isOpen ? 'active' : 'inactive')
          )}
          style={{
            [`margin${isToRight ? 'Right' : 'Left'}`]: isOpen ? width : minWidth
          }}
        >
          {pageContentEl}
        </main>
      );
    }

    return mainContent;
  }, [page, currentState, isOpen, minWidth, isToRight, width]);

  let sidebarStyle = { width };

  if (minWidth) {
    sidebarStyle = { width: isOpen ? width : minWidth };
  }

  const sidebar = (
    <aside
      className={classnames(
        addVariants(
          'aglmd-drawer',
          styles,
          minWidth ? 'has-min-width' : 'no-min-width',
          isToRight ? 'right' : 'left',
          isOpen ? 'active' : 'inactive',
          variants
        ),
        className
      )}
      style={sidebarStyle}
      {...props}
    >
      <div
        className={classnames(
          addVariants('aglmd-drawer__content', styles, isOpen ? 'active' : 'inactive')
        )}
      >
        {sidebarContentEl}
      </div>
    </aside>
  );

  if (!mainContentEl) {
    return sidebar;
  }

  return (
    <div className={classnames(styles['aglmd-drawer__container'], 'aglmd-drawer__container')}>
      {isToRight ? mainContentEl : sidebar}
      {!isToRight ? mainContentEl : sidebar}
    </div>
  );
};

Drawer.propTypes = {
  /**
   * Show drawer to the right of the screen
   */
  isToRight: PropTypes.bool,
  /**
   * The main page that this drawer accompanies, if a function is supplied:
   * isOpen: Boolean, openDrawer: Function, closeDrawer: Function, toggleDrawer: Function
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /**
   * The main page that this drawer accompanies, if a function is supplied:
   * isOpen: Boolean, openDrawer: Function, closeDrawer: Function, toggleDrawer: Function
   *
   * If not specified the drawer will overlap the page
   */
  page: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.bool]),
  isPermanent: PropTypes.bool,
  /**
   * Whether drawer is open, state must be handled by the caller
   */
  isOpen: PropTypes.bool,
  /**
   * Whether the drawer is open by default
   */
  isOpenByDefault: PropTypes.bool,
  onChange: PropTypes.func,
  /**
   * Decides the variant of the list item
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  width: PropTypes.number,
  minWidth: PropTypes.number
};

Drawer.defaultProps = {
  page: false,
  isToRight: false,
  isPermanent: false,
  isOpen: null,
  isOpenByDefault: true,
  onChange: () => 0,
  variant: 'default',
  className: '',
  width: 240,
  minWidth: 0
};

export default Drawer;
